import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { IAlertComponent } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { Audience } from "@front/m19-api-client";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { map, switchMap } from "rxjs";
import { AudiencesService } from "@front/m19-services";

@Component({
  selector: "app-dsp-audience-warning",
  imports: [IAlertComponent, TranslocoDirective],
  template: ` @if (hasFailedAudiences() || hasPendingAudiences()) {
    <IAlert *transloco="let t" class="mt-3 block" [type]="hasFailedAudiences() ? 'error' : 'warning'">
      @if (hasFailedAudiences()) {
        {{ t("dsp-audiences-warning." + failedAudiencesMessage()) }}
      } @else if (hasPendingAudiences()) {
        {{ t("dsp-audiences-warning." + pendingAudiencesMessage()) }}
        {{ t("dsp-audiences-warning.strategy_wont_run_until_audiences_validated") }}
      }
    </IAlert>
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspAudienceWarningComponent {
  private readonly audiencesService = inject(AudiencesService);

  smartAudienceMode = input<boolean>(false);
  audiences = input.required<Audience[][]>();

  private pendingOrFailedAudiences$ = toObservable(this.audiences).pipe(
    switchMap((audiences) => this.audiencesService.getPendingOrFailedAudiences(audiences)),
  );

  hasFailedAudiences = toSignal(
    this.pendingOrFailedAudiences$.pipe(map(({ failedAudiences }) => failedAudiences.length > 0)),
  );
  failedAudiencesMessage = computed(() =>
    this.smartAudienceMode() ? "a_smart_audience_has_failed_description" : "an_audience_has_failed",
  );

  hasPendingAudiences = toSignal(
    this.pendingOrFailedAudiences$.pipe(map(({ pendingAudiences }) => pendingAudiences.length > 0)),
  );

  pendingAudiencesMessage = computed(() =>
    this.smartAudienceMode()
      ? "you_have_selected_pending_or_processing_smart_audiences"
      : "you_have_selected_pending_or_processing_audiences",
  );
}
